import { useTranslation } from "react-i18next";

import { isSetupFeeRow, PlanInfoNote } from "./planRows";
import type { IRepaymentPlanData } from "./types";

interface IRepaymentPlanCompactTableProps {
  plan: IRepaymentPlanData;
  formatCurrency: (value: string | number) => string;
}

/**
 * The collapsed plan that fits the half-width Key calculations box: month,
 * payment and remaining balance only. Interest and principal live in the
 * expanded view.
 */
export const RepaymentPlanCompactTable = ({
  plan,
  formatCurrency,
}: IRepaymentPlanCompactTableProps) => {
  const { t } = useTranslation("repaymentPlan");

  return (
    <div
      className="max-h-64 overflow-auto rounded-lg border border-gray-200"
      data-testid="repayment-plan-compact"
    >
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th className="text-table-header sticky top-0 z-10 bg-gray-100 py-2 pl-3 pr-2 text-left">
              {t("month")}
            </th>
            <th className="text-table-header sticky top-0 z-10 bg-gray-100 py-2 pr-3 text-right">
              {t("payment")}
            </th>
            <th className="text-table-header sticky top-0 z-10 bg-gray-100 py-2 pr-3 text-right">
              {t("remainingBalance")}
            </th>
          </tr>
        </thead>
        <tbody>
          {plan.entries.map((entry) => (
            <tr
              key={entry.month}
              data-testid="compact-row"
              className="bg-white"
            >
              <td className="border-t py-2 pl-3 pr-2 text-table-cell">
                {entry.month}
              </td>
              <td className="border-t py-2 pr-3 text-table-cell">
                <div className="flex items-center justify-end gap-1">
                  <span className="tabular-nums">
                    {formatCurrency(Number(entry.payment))}
                  </span>
                  {isSetupFeeRow(entry) && (
                    <PlanInfoNote
                      label={t("setupFee")}
                      text={t("tooltips.setupFee")}
                    />
                  )}
                </div>
              </td>
              <td className="border-t py-2 pr-3 text-right text-table-cell tabular-nums">
                {formatCurrency(Number(entry.remainingBalance))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
